import React from 'react';

import type * as api from '@gitbook/api-client';
import type { ContentRefColumnDefinition } from '@gitbook/doc-core';
import { useHiveClient } from '@gitbook/hive-react';
import { createPalette, usePaletteHandle } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { Link, LoadingIndicator, renderIcon } from '@gitbook/spine-react';

import {
    createInputContentRefPalette,
    useContentReference,
    useContentReferenceContext,
} from '../../../ContentReferences';
import { InsertValueButton } from './buttons';
import type { RecordValueRenderProps, TableDefinitionRenderSpec } from './types';

export const contentRefDefinitionSpec: TableDefinitionRenderSpec<
    api.ContentRef | null,
    ContentRefColumnDefinition
> = {
    title: 'Link',
    description: 'Reference another page, file, space or collection.',
    icon: Icon.AddLink,
    ValueComponent: Recordvalue,
    onKeyDown: (key, value, setValue) => {
        switch (key) {
            case 'backspace':
                if (value) {
                    setValue(null);
                }
                break;
        }
    },
};

/**
 * Cell content for a ContentRef column.
 */
function Recordvalue(
    props: RecordValueRenderProps<api.ContentRef | null, ContentRefColumnDefinition>
): React.ReactElement {
    const { value, selected, readOnly, displayMode, onValueChange, onKeyDown } = props;

    const [deferredSelected, setDeferredSelected] = React.useState(selected);
    const buttonRef = React.useRef<HTMLButtonElement>(null);

    React.useEffect(() => {
        setDeferredSelected(selected);
        // If the current cell is `selected`, focus the + button
        return selected ? buttonRef.current?.focus() : buttonRef.current?.blur();
    }, [selected]);
    const paletteHandle = usePaletteHandle(buttonRef, {
        onClose: () => {
            buttonRef.current?.focus();
        },
    });
    const contentReferenceContext = useContentReferenceContext();
    const hiveClient = useHiveClient();

    const handleAdd = () => {
        paletteHandle.open(
            createInputContentRefPalette(onValueChange, contentReferenceContext, hiveClient, {
                accept: ['page', 'space', 'anchor', 'file', 'url'],
            })
        );
    };

    const handleEdit = () => {
        paletteHandle.open(
            createPalette({
                groups: [
                    {
                        key: 'edit',
                        commands: [
                            {
                                key: 'edit',
                                title: 'Edit link',
                                icon: Icon.Edit,
                                trigger: () =>
                                    createInputContentRefPalette(
                                        onValueChange,
                                        contentReferenceContext,
                                        hiveClient,
                                        {
                                            accept: [
                                                'page',
                                                'file',
                                                'space',
                                                'collection',
                                                'anchor',
                                                'url',
                                            ],
                                        }
                                    ),
                            },
                            {
                                key: 'delete',
                                title: 'Remove link',
                                icon: Icon.Trashcan,
                                trigger: () => onValueChange(null),
                            },
                        ],
                    },
                ],
            })
        );
    };

    // Only show the controls when this RecordValue is used in the modal. In cards + table, we show controls in a different way.
    const showControls = displayMode === 'modal' && !readOnly;

    return (
        <div className="relative flex flex-1 flex-wrap items-center">
            <React.Suspense fallback={<LoadingIndicator />}>
                {value ? (
                    <>
                        <div className="flex flex-1">
                            <ContentReferenceLink
                                contentRef={value}
                                disabled={!readOnly && !deferredSelected}
                            />
                        </div>
                        {showControls && (
                            <InsertValueButton
                                buttonRef={buttonRef}
                                leadingIcon={Icon.Settings}
                                label="Change target"
                                active={paletteHandle.opened}
                                onKeyDown={onKeyDown}
                                onPress={handleEdit}
                                tooltip={{
                                    label: 'Change target',
                                    shortcut: 'space',
                                }}
                            />
                        )}
                    </>
                ) : showControls ? (
                    <InsertValueButton
                        buttonRef={buttonRef}
                        leadingIcon={Icon.Pencil}
                        label="Add link"
                        active={paletteHandle.opened}
                        onKeyDown={onKeyDown}
                        onPress={handleAdd}
                    />
                ) : null}
            </React.Suspense>
        </div>
    );
}

/**
 * Render the clickable link. Wrapped in a suspense boundary as it suspends while resolving the reference.
 */
function ContentReferenceLink(props: {
    contentRef: api.ContentRef;
    disabled: boolean;
}): React.ReactElement | null {
    const { contentRef } = props;
    const resolved = useContentReference(contentRef);

    if (!resolved) {
        return null;
    }

    return (
        <div className="flex flex-1 items-center gap-2">
            {renderIcon(resolved.icon, { size: 18, className: 'text-muted shrink-0' })}
            <Link
                href={props.disabled ? undefined : resolved.href}
                onPress={() => {
                    resolved.open();
                }}
                className="text-wrap"
            >
                {resolved.title}
            </Link>
        </div>
    );
}
